"use client";

import { useRef, useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";

import { Input, Label } from "@/components/ui";

import getGoogleMapsPlacesApiClient from "@/lib/google-places-client";
import { cx } from "@/lib/tailwind-utils";

interface AddressInputProps {
  onAddressChange?: (address: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
    latitude?: number;
    longitude?: number;
  }) => void;
  className?: string;
  placeholder?: string;
  label?: string;
}

interface Suggestion {
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export default function AddressInput({
  onAddressChange,
  className,
  placeholder = "Enter your address",
  label = "Your Address",
}: AddressInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const loadSuggestions = async (inputValue: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Don't load suggestions if not enough characters
    if (!inputValue || inputValue.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Debounce the loading of suggestions to reduce usage of the Google API
    timeoutRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const places = await getGoogleMapsPlacesApiClient();
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new places.AutocompleteSessionToken();
        }

        new places.AutocompleteService().getPlacePredictions(
          {
            input: inputValue,
            sessionToken: sessionTokenRef.current,
            componentRestrictions: { country: "us" },
          },
          (predictions, status) => {
            setIsLoading(false);
            if (status === places.PlacesServiceStatus.ZERO_RESULTS) {
              setSuggestions([]);
              setShowSuggestions(false);
              return;
            }
            if (status !== places.PlacesServiceStatus.OK || !predictions) {
              setSuggestions([]);
              setShowSuggestions(false);
              return;
            }
            setSuggestions(predictions);
            setShowSuggestions(true);
          },
        );
      } catch (error) {
        setIsLoading(false);
        console.error("Error loading address suggestions:", error);
      }
    }, 300);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    loadSuggestions(value);
  };

  const handleSuggestionSelected = async (suggestion: Suggestion) => {
    setInputValue(suggestion.structured_formatting.main_text);
    setSuggestions([]);
    setShowSuggestions(false);

    try {
      const places = await getGoogleMapsPlacesApiClient();
      const sessionToken = sessionTokenRef.current || undefined;
      sessionTokenRef.current = null;

      const attributionContainer = document.getElementById(
        "googlemaps-attribution-container",
      );
      if (!attributionContainer) {
        // Create container if it doesn't exist
        const container = document.createElement("div");
        container.id = "googlemaps-attribution-container";
        container.style.display = "none";
        document.body.appendChild(container);
      }

      const container = document.getElementById(
        "googlemaps-attribution-container",
      );
      if (container) {
        new places.PlacesService(container as HTMLDivElement).getDetails(
          {
            placeId: suggestion.place_id,
            fields: ["address_components", "geometry"],
            sessionToken,
          },
          (place, status) => {
            if (status === places.PlacesServiceStatus.OK && place) {
              const getAddressComponent = (
                type: string,
                short_name: boolean = false,
              ) =>
                place.address_components?.find((component: any) =>
                  component.types.includes(type),
                )?.[short_name ? "short_name" : "long_name"] || "";

              const streetNumber = getAddressComponent("street_number");
              const route = getAddressComponent("route");
              const address = `${streetNumber} ${route}`.trim();
              const city =
                getAddressComponent("locality") ||
                getAddressComponent("sublocality");
              const state = getAddressComponent(
                "administrative_area_level_1",
                true,
              );
              const zipCode = getAddressComponent("postal_code");

              let latitude: number | undefined;
              let longitude: number | undefined;

              if (place.geometry?.location) {
                latitude = place.geometry.location.lat();
                longitude = place.geometry.location.lng();
              }

              onAddressChange?.({
                address,
                city,
                state,
                zipCode,
                latitude,
                longitude,
              });
            }
          },
        );
      }
    } catch (error) {
      console.error("Error getting place details:", error);
    }
  };

  const handleInputBlur = () => {
    // Small delay to allow clicking on suggestions
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className={cx("relative", className)}>
      <div id="googlemaps-attribution-container" style={{ display: "none" }} />

      <div className="space-y-2">
        <Label htmlFor="address-input">{label}</Label>
        <div className="relative">
          <Input
            id="address-input"
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className="pr-10"
          />
          <FaMapMarkerAlt className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background/90 shadow-lg backdrop-blur-sm">
          <div className="max-h-60 overflow-auto p-1">
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.place_id}
                className="cursor-pointer rounded px-3 py-2 hover:bg-muted"
                onMouseDown={(e) => {
                  // Prevent the input from losing focus
                  e.preventDefault();
                }}
                onClick={() => handleSuggestionSelected(suggestion)}
              >
                <div className="text-body text-foreground">
                  {suggestion.structured_formatting.main_text}
                </div>
                <div className="text-caption text-muted-foreground">
                  {suggestion.structured_formatting.secondary_text}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background/90 p-3 shadow-lg backdrop-blur-sm">
          <div className="text-body text-muted-foreground">
            Loading suggestions...
          </div>
        </div>
      )}
    </div>
  );
}
